import { supabase } from '../../../../supabaseConfig';

/**
 * Serviço para gerenciamento de saldos de alunos - Nova estrutura database-driven
 * Agora usa as functions do banco e views otimizadas
 */
export class SaldosAlunosService {

     /**
      * Busca saldo de um aluno específico
      */
     static async getSaldoByAluno(organizationId: number, alunoId: number) {
          try {
               const response = await supabase
                    .from('vw_saldos_alunos')
                    .select('*')
                    .eq('organizacao', organizationId)
                    .eq('id_aluno', alunoId)
                    .single();

               return response;
          } catch (error) {
               console.error('Erro ao buscar saldo do aluno:', error);
               throw error;
          }
     }

     /**
      * Adiciona crédito ao saldo do aluno (via transferência)
      */
     static async adicionarCredito(
          organizationId: number,
          alunoId: number,
          responsavelId: number,
          valor: number,
          userId?: number
     ) {
          try {
               // Usar function para transferência (mais seguro)
               const response = await supabase.rpc('fn_transferir_credito_aluno', {
                    p_id_responsavel: responsavelId,
                    p_id_aluno: alunoId,
                    p_valor: valor,
                    p_id_organizacao: organizationId,
                    p_created_by: userId || responsavelId
               });

               return response;
          } catch (error) {
               console.error('Erro ao adicionar crédito:', error);
               throw error;
          }
     }

     /**
      * Debita valor do saldo do aluno (para compras)
      */
     static async debitarSaldo(
          organizationId: number,
          alunoId: number,
          valor: number,
          _vendaId: number,
          descricao: string,
          userId?: number
     ) {
          try {
               // Usar function para processar venda
               const response = await supabase.rpc('fn_processar_venda_totem', {
                    p_id_aluno: alunoId,
                    p_valor_venda: valor,
                    p_descricao_venda: descricao,
                    p_id_organizacao: organizationId,
                    p_created_by: userId
               });

               return response;
          } catch (error) {
               console.error('Erro ao debitar saldo:', error);
               throw error;
          }
     }

     /**
      * Define limites de gasto para o aluno
      */
     static async definirLimites(
          organizationId: number,
          alunoId: number,
          limiteDiario: number,
          limiteSemanal: number,
          _userId?: number
     ) {
          try {
               const response = await supabase
                    .from('saldos_alunos')
                    .update({
                         limite_diario: limiteDiario,
                         limite_semanal: limiteSemanal,
                         updated_at: new Date().toISOString()
                    })
                    .eq('fk_id_aluno', alunoId)
                    .eq('fk_id_organizacao', organizationId)
                    .select();

               return response;
          } catch (error) {
               console.error('Erro ao definir limites:', error);
               throw error;
          }
     }

     /**
      * Calcula gastos do aluno em um dia específico
      */
     static async calcularGastosDiarios(
          organizationId: number,
          alunoId: number,
          data: string
     ): Promise<number> {
          try {
               const response = await supabase
                    .from('movimentacoes_saldo')
                    .select('valor')
                    .eq('fk_id_pessoa', alunoId)
                    .eq('fk_id_organizacao', organizationId)
                    .eq('tipo_movimentacao', 'debito')
                    .eq('tipo_origem', 'venda')
                    .gte('data_movimentacao', data + 'T00:00:00')
                    .lte('data_movimentacao', data + 'T23:59:59');

               return response.data?.reduce((sum, item) => sum + item.valor, 0) || 0;
          } catch (error) {
               console.error('Erro ao calcular gastos diários:', error);
               return 0;
          }
     }

     /**
      * Verifica se o aluno pode realizar uma compra
      */
     static async verificarLimiteCompra(
          organizationId: number,
          alunoId: number,
          valorCompra: number,
     ): Promise<{
          podeComprar: boolean;
          motivo?: string;
          saldoDisponivel: number;
          limiteDiarioRestante: number;
          limiteSemanalRestante: number;
     }> {
          try {
               const saldoResponse = await this.getSaldoByAluno(organizationId, alunoId);
               const saldo = saldoResponse.data;

               if (!saldo) {
                    return {
                         podeComprar: false,
                         motivo: 'Aluno não possui saldo cadastrado',
                         saldoDisponivel: 0,
                         limiteDiarioRestante: 0,
                         limiteSemanalRestante: 0,
                    };
               }

               // Verificar saldo disponível
               if (!saldo.saldo_disponivel || saldo.saldo_disponivel < valorCompra) {
                    return {
                         podeComprar: false,
                         motivo: 'Saldo insuficiente',
                         saldoDisponivel: saldo.saldo_disponivel || 0,
                         limiteDiarioRestante: 0,
                         limiteSemanalRestante: 0,
                    };
               }

               // Verificar limite diário 
               if (saldo.limite_diario && saldo.limite_diario > 0) {
                    // Calcular gastos do dia atual
                    const hoje = new Date().toISOString().split('T')[0];
                    const gastosHoje = await this.calcularGastosDiarios(organizationId, alunoId, hoje);
                    
                    if (gastosHoje + valorCompra > saldo.limite_diario) {
                         return {
                              podeComprar: false,
                              motivo: 'Limite diário excedido',
                              saldoDisponivel: saldo.saldo_disponivel || 0,
                              limiteDiarioRestante: (saldo.limite_diario || 0) - gastosHoje,
                              limiteSemanalRestante: 0,
                         };
                    }
               }

               // Verificar limite semanal (se configurado)
               if (saldo.limite_semanal && saldo.limite_semanal > 0) {
                    // Calcular gastos da semana
                    const hoje = new Date();
                    const inicioSemana = new Date(hoje.setDate(hoje.getDate() - hoje.getDay()));
                    const fimSemana = new Date(hoje.setDate(hoje.getDate() - hoje.getDay() + 6));

                    const gastosResponse = await supabase
                         .from('movimentacoes_saldo')
                         .select('valor')
                         .eq('fk_id_pessoa', alunoId)
                         .eq('fk_id_organizacao', organizationId)
                         .eq('tipo_movimentacao', 'debito')
                         .eq('tipo_origem', 'venda')
                         .gte('data_movimentacao', inicioSemana.toISOString())
                         .lte('data_movimentacao', fimSemana.toISOString());

                    const gastoSemana = gastosResponse.data?.reduce((sum, item) => sum + item.valor, 0) || 0;
                    const limiteSemanalRestante = saldo.limite_semanal - gastoSemana;

                    if (limiteSemanalRestante < valorCompra) {
                         return {
                              podeComprar: false,
                              motivo: 'Limite semanal excedido',
                              saldoDisponivel: saldo.saldo_disponivel || 0,
                              limiteDiarioRestante: 0,
                              limiteSemanalRestante,
                         };
                    }
               }

               return {
                    podeComprar: true,
                    saldoDisponivel: saldo.saldo_disponivel || 0,
                    limiteDiarioRestante: saldo.limite_diario && saldo.limite_diario > 0 ? saldo.limite_diario : 999999,
                    limiteSemanalRestante: saldo.limite_semanal && saldo.limite_semanal > 0 ? saldo.limite_semanal : 999999,
               };
          } catch (error) {
               console.error('Erro ao verificar limite de compra:', error);
               throw error;
          }
     }

     /**
      * Busca todos os saldos com informações dos alunos
      */
     static async getAllWithAlunos(organizationId: number) {
          try {
               const response = await supabase
                    .from('vw_saldos_alunos')
                    .select('*')
                    .eq('organizacao', organizationId)
                    .order('saldo_disponivel', { ascending: false });

               return response;
          } catch (error) {
               console.error('Erro ao buscar todos os saldos:', error);
               throw error;
          }
     }

     /**
      * Busca saldos dos alunos de um responsável específico
      */
     static async getSaldosByResponsavel(organizationId: number, responsavelId: number) {
          try {
               const response = await supabase
                    .from('vw_saldos_alunos')
                    .select('*')
                    .eq('organizacao', organizationId)
                    .eq('id_responsavel', responsavelId)
                    .order('saldo_disponivel', { ascending: false });

               return response;
          } catch (error) {
               console.error('Erro ao buscar saldos por responsável:', error);
               throw error;
          }
     }

     /**
      * Relatório de consumo do aluno
      */
     static async getRelatorioConsumo(organizationId: number, alunoId: number) {
          try {
               const response = await supabase
                    .from('vw_historico_financeiro')
                    .select('*')
                    .eq('fk_id_organizacao', organizationId)
                    .eq('fk_id_pessoa', alunoId)
                    .order('data_movimentacao', { ascending: false });

               return response;
          } catch (error) {
               console.error('Erro ao buscar relatório de consumo:', error);
               throw error;
          }
     }

     /**
      * Busca histórico de transferências recebidas
      */
     static async getHistoricoTransferencias(organizationId: number, alunoId: number) {
          try {
               const response = await supabase
                    .from('vw_historico_financeiro')
                    .select('*')
                    .eq('fk_id_organizacao', organizationId)
                    .eq('fk_id_pessoa', alunoId)
                    .eq('tipo_movimentacao', 'credito')
                    .eq('tipo_origem', 'transferencia')
                    .order('data_movimentacao', { ascending: false });

               return response;
          } catch (error) {
               console.error('Erro ao buscar histórico de transferências:', error);
               throw error;
          }
     }
}

export const saldosAlunosService = new SaldosAlunosService();
